import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  AlertTriangle, Stethoscope, Search, Scissors, Pill, Flame, Heart,
  Clock, Calendar, MessageCircle, Phone, ArrowLeft, ArrowRight, ChevronRight
} from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

const symptoms = [
  "Abnormal vaginal bleeding",
  "Pelvic pain or pressure",
  "Ovarian cysts or masses",
  "Suspicious findings on ultrasound or scans",
  "Early-stage cancer diagnosis",
];

const whenToConsult = [
  "You have been diagnosed with early-stage gynecologic cancer",
  "Surgery has been advised",
  "You are looking for minimally invasive treatment options",
  "You want faster recovery and less surgical discomfort",
  "You need a second opinion regarding surgical approach",
];

const diagnostics = [
  { label: "", detail: "Imaging tests (ultrasound, CT scan, MRI)" },
  { label: "", detail: "Blood tests and tumor markers" },
  { label: "", detail: "Assessment of cancer stage" },
  { label: "", detail: "Fitness for anesthesia and surgery" },
];

const treatments = [
  {
    icon: Scissors,
    title: "Laparoscopic Surgery Procedure",
    description:
      "Minimally invasive surgery is performed using advanced laparoscopic techniques.",
    points: [
      "Small incisions are made in the abdomen",
      "A camera (laparoscope) is inserted",
      "Specialized instruments are used for precise surgery",
      "Cancerous tissue is removed carefully",
    ],
  },
  {
    icon: Pill,
    title: "Types of Procedures",
    description: "Depending on the condition, procedures may include:",
    points: [
      "Laparoscopic hysterectomy",
      "Endometriosis resection",
      "Removal of ovaries and fallopian tubes",
      "Cancer staging procedures",
      "Lymph node assessment",
      "Omentectomy",
      "Cytoreductive procedures (selected cases)",
    ],
  },
  {
    icon: Heart,
    title: "Conversion to Open Surgery (if needed)",
    description:
      "In some situations, open surgery may be required for optimal safety and treatment.",
    points: [
      "Open surgery may be required for safety",
      "May be needed for better cancer clearance",
      "Patient safety is always the top priority",
    ],
  },
];

const recoveryTimeline = [
  {
    phase: "Hospital Stay",
    items: [
      "Typically 1–2 days",
    ],
  },
  {
    phase: "Initial Recovery",
    items: [
      "1–2 weeks for basic activities",
    ],
  },
  {
    phase: "Full Recovery",
    items: [
      "3–4 weeks (faster than open surgery)",
    ],
  },
  {
    phase: "Follow-Up",
    items: [
      "Regular monitoring",
      "Additional treatment if required",
    ],
  },
];

const faqs = [
  {
    q: "Is laparoscopic cancer surgery safe?",
    a: "Yes, laparoscopic surgery is considered safe and effective when performed by experienced specialists in suitable patients.",
  },
  {
    q: "Is laparoscopic surgery better than open surgery?",
    a: "For selected patients, laparoscopic surgery offers benefits like smaller scars, less pain, and faster recovery.",
  },
  {
    q: "Can all gynecologic cancers be treated laparoscopically?",
    a: "No, suitability depends on the type, stage, and spread of cancer.",
  },
  {
    q: "Will laparoscopic surgery leave scars?",
    a: "Only small scars are usually present because the surgery is performed through tiny incisions.",
  },
  {
    q: "How soon can I return to normal activities?",
    a: "Most patients recover faster compared to open surgery and gradually resume daily activities within a few weeks.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55 } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
};

export default function OvarianCancerPage() {
  return (
    <>
      <Helmet>
        <title>
          Laparoscopic Cancer Surgery in Kolkata | Dr Neha Agarwal
        </title>

        <meta
          name="description"
          content="Consult Dr Neha Agarwal for advanced laparoscopic cancer surgery in Kolkata offering minimally invasive gynecologic oncology treatment with faster recovery."
        />

        <meta
          name="keywords"
          content="laparoscopic cancer surgery Kolkata, minimally invasive gynecologic surgery Kolkata, laparoscopic gynecologic oncologist Kolkata"
        />
      </Helmet>
      <div className="bg-[#F9F9F6] min-h-screen">
        {/* ─── STICKY HEADER ─── */}
        {/* <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#F9F9F6]/80 border-b border-[#1E3F33]/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center gap-2 group" data-testid="service-header-logo">
              <span className="font-serif text-2xl md:text-3xl font-semibold text-[#1E3F33] tracking-tight">
                Dr. Neha Agarwal
              </span>
            </Link>
            <div className="flex items-center gap-4">
              <Link
                to="/"
                data-testid="service-back-home"
                className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-[#4A6359] hover:text-[#1E3F33] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
              <a
                href="tel:+916291000829"
                data-testid="service-header-call"
                className="inline-flex items-center gap-2 bg-[#1E3F33] text-[#F9F9F6] px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#2C5545] transition-colors"
              >
                <Phone className="w-4 h-4" />
                Call Now
              </a>
            </div>
          </div>
        </header> */}

        {/* ─── HERO / INTRO ─── */}
        <section className="pt-28 md:pt-36 pb-16 md:pb-24" data-testid="service-hero">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            {/* Breadcrumb */}
            <motion.nav
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="flex items-center gap-2 text-sm text-[#4A6359] mb-8"
              data-testid="service-breadcrumb"
            >
              <Link to="/" className="hover:text-[#1E3F33] transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <Link to="/#services" className="hover:text-[#1E3F33] transition-colors">Services</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-[#1E3F33] font-medium">Laparoscopic Cancer Surgery </span>
            </motion.nav>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="lg:col-span-7"
              >
                <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                  Gynecologic Oncology
                </p>
                <h1
                  data-testid="service-page-title"
                   style={{ lineHeight: "1.2" }}
                  className="font-serif text-4xl sm:text-3xl lg:text-6xl xl:text-5xl tracking-tighter leading-[0.95] text-[#1E3F33] mb-6"
                >
                  Laparoscopic Cancer Surgery in Kolkata – Minimally Invasive Gynecologic Oncology Care
                </h1>
                <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed max-w-2xl mb-8" data-testid="service-intro">
                  Minimally invasive (laparoscopic) surgery has transformed cancer treatment by offering safer procedures, faster recovery, and less post-operative discomfort.
                </p>
                <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed max-w-2xl mb-8" data-testid="service-intro">
                  Dr. Neha Agarwal, an experienced gynecologic oncologist in Kolkata, specializes in advanced laparoscopic cancer surgery for gynecologic cancers, including ovarian, uterine, and early-stage cervical cancers.
                </p>
                <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed max-w-2xl mb-8" data-testid="service-intro">
                  Her approach focuses on precision, reduced surgical trauma, and quicker return to normal life while maintaining the highest standards of cancer care.</p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="/book-appointment"
                    data-testid="service-hero-book-btn"
                    className="inline-flex items-center gap-2 bg-[#1E3F33] text-[#F9F9F6] px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-[#2C5545] transition-colors"
                  >
                    <Calendar className="w-4 h-4" />
                    Book Appointment
                  </a>
                  <a
                    href="https://wa.me/916291000829"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="service-hero-whatsapp-btn"
                    className="inline-flex items-center gap-2 border-2 border-[#1E3F33] text-[#1E3F33] px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-[#1E3F33] hover:text-[#F9F9F6] transition-all"
                  >
                    <MessageCircle className="w-4 h-4" />
                    WhatsApp
                  </a>
                </div>
              </motion.div>

              {/* Quick Info Card */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="lg:col-span-5"
              >
                <div className="bg-[#F2EFE9] rounded-2xl md:rounded-[2rem] p-7 md:p-8" data-testid="service-quick-info">
                  <h3 className="font-serif text-2xl text-[#1E3F33] mb-5">
                    Quick Overview
                  </h3>

                  <div className="space-y-4">

                    {/* Specialist */}
                    <div className="flex items-start gap-3">
                      <Stethoscope className="w-5 h-5 text-[#8FA998] mt-0.5 flex-shrink-0" />

                      <div>
                        <p className="text-sm font-semibold text-[#1E3F33]">
                          Specialist
                        </p>

                        <p className="text-sm text-[#4A6359]">
                          Dr. Neha Agarwal, Gynecologic Oncologist
                        </p>
                      </div>
                    </div>

                    {/* Surgery Type */}
                    <div className="flex items-start gap-3">
                      <Scissors className="w-5 h-5 text-[#8FA998] mt-0.5 flex-shrink-0" />

                      <div>
                        <p className="text-sm font-semibold text-[#1E3F33]">
                          Surgical Approach
                        </p>

                        <p className="text-sm text-[#4A6359]">
                          Minimally Invasive Laparoscopic Cancer Surgery
                        </p>
                      </div>
                    </div>

                    {/* Evaluation */}
                    <div className="flex items-start gap-3">
                      <Search className="w-5 h-5 text-[#8FA998] mt-0.5 flex-shrink-0" />

                      <div>
                        <p className="text-sm font-semibold text-[#1E3F33]">
                          Evaluation
                        </p>

                        <p className="text-sm text-[#4A6359]">
                          Imaging Tests, Cancer Staging, Tumor Markers & Surgical Fitness Assessment
                        </p>
                      </div>
                    </div>

                    {/* Key Message */}
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-[#8FA998] mt-0.5 flex-shrink-0" />

                      <div>
                        <p className="text-sm font-semibold text-[#1E3F33]">
                          Key Message
                        </p>

                        <p className="text-sm text-[#4A6359]">
                          Minimally invasive surgery offers faster recovery and reduced post-operative discomfort in suitable patients
                        </p>
                      </div>
                    </div>

                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
        {/* ─── WHAT IS LAPAROSCOPIC SURGERY SECTION ─── */}
        <section className="py-16 md:py-24 bg-[#F2EFE9]" data-testid="laparoscopic-about-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">

              {/* Left Content */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="lg:col-span-7"
              >
                <h2 className="font-serif text-4xl sm:text-5xl tracking-tighter leading-[1] text-[#1E3F33] mb-6">
                  What is Laparoscopic Cancer Surgery?
                </h2>

                <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed mb-6">
                  Laparoscopic surgery is a minimally invasive technique where
                  procedures are performed through small incisions using a camera
                  and specialized instruments.
                </p>

                <div className="space-y-6">
                  <div className="bg-white rounded-2xl p-6 border border-[#D8DDD9]">
                    <h3 className="font-serif text-2xl text-[#1E3F33] mb-4">
                      Key Features
                    </h3>

                    <div className="space-y-3">
                      {[
                        "Small incisions instead of large cuts",
                        "High-definition camera for precision",
                        "Less blood loss",
                        "Reduced pain after surgery",
                        "Shorter hospital stay",
                      ].map((item, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <div className="w-2.5 h-2.5 rounded-full bg-[#8FA998] mt-2"></div>

                          <p className="text-[#4A6359] leading-relaxed">
                            {item}
                          </p>
                        </div>
                      ))}
                    </div>

                    <p className="text-[#4A6359] leading-relaxed mt-5">
                      This technique is widely used in modern cancer treatment whenever suitable.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Right Card */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="lg:col-span-5"
              >
                <div className="bg-[#1E3F33] rounded-2xl md:rounded-[2rem] p-7 md:p-8 text-[#F9F9F6]">
                  <h3 className="font-serif text-3xl mb-6">
                    Conditions Treated with Laparoscopic Surgery
                  </h3>

                  <div className="space-y-5">
                    {[
                      "Early-stage ovarian cancer",
                      "Uterine (endometrial) cancer",
                      "Selected cervical cancer cases",
                      "Benign gynecologic conditions (when associated)",
                      "Diagnostic procedures and staging",
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-2.5 h-2.5 rounded-full bg-[#8FA998] mt-2"></div>

                        <p className="text-sm sm:text-base leading-relaxed text-[#F9F9F6]/90">
                          {item}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 bg-[#2C5545] border border-[#8FA998]/20 rounded-xl p-5">
                    <p className="text-sm sm:text-base leading-relaxed text-[#F9F9F6]">
                      Suitability depends on cancer stage and patient condition.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
        {/* ─── SYMPTOMS ─── */}
        <section className="py-20 md:py-28 bg-[#1E3F33]" data-testid="service-symptoms-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={stagger}
            >
              <motion.div variants={fadeUp} className="mb-10">
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle className="w-5 h-5 text-[#C29F60]" />
                  <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#C29F60]">
                    Warning Signs
                  </p>
                </div>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F9F9F6] leading-tight max-w-2xl" data-testid="service-symptoms-heading">
                  Symptoms / When This Surgery May Be Needed
                </h2>
                <p className="text-base sm:text-lg text-[#8FA998] mt-4 max-w-2xl leading-relaxed">
                  You may require surgical evaluation if you experience
                </p>
              </motion.div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                {symptoms.map((s, i) => (
                  <motion.div
                    key={i}
                    variants={fadeUp}
                    data-testid={`service-symptom-${i}`}
                    className="bg-[#2C5545] rounded-xl p-5 border border-[#8FA998]/15"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#C29F60]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-[#C29F60]">{i + 1}</span>
                      </div>
                      <p className="text-[#F9F9F6] text-sm sm:text-base font-medium">{s}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div
                variants={fadeUp}
                className="relative overflow-hidden rounded-2xl border-2 border-[#C29F60]/60 bg-gradient-to-r from-[#2C5545] to-[#234538] p-6 sm:p-7 shadow-[0_10px_40px_rgba(0,0,0,0.25)]"
              >
                {/* Soft Highlight */}
                <div className="absolute inset-0 bg-[#C29F60]/5 pointer-events-none" />

                <div className="relative flex items-start gap-4">

                  {/* Icon */}
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#C29F60] text-[#163828] font-bold text-xl shadow-lg">
                    ✓
                  </div>

                  {/* Content */}
                  <div>
                    <p className="text-[#F4E7CC] text-lg sm:text-xl font-semibold leading-relaxed">
                      A specialist consultation determines whether{" "}
                      <span className="text-[#F6C978] font-bold">
                        laparoscopic surgery
                      </span>{" "}
                      is appropriate.
                    </p>

                    <p className="mt-2 text-[#D7D7D7] text-sm sm:text-base">
                      Personalized evaluation ensures the safest and most effective surgical approach for each patient.
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ─── WHEN TO CONSULT ─── */}
        <section className="py-20 md:py-28 bg-[#F9F9F6]" data-testid="service-consult-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
              >
                <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                  Expert Guidance
                </p>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight" data-testid="service-consult-heading">
                  When to Consult a Specialist
                </h2>
                <p className="text-base sm:text-lg text-[#4A6359] mt-4 leading-relaxed">
                  Consult a gynecologic oncologist in Kolkata if:
                </p>
              </motion.div>

              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={stagger}
                className="space-y-0"
              >
                {whenToConsult.map((item, i) => (
                  <motion.div
                    key={i}
                    variants={fadeUp}
                    data-testid={`service-consult-item-${i}`}
                    className="flex items-start gap-4 py-5 border-b border-[#D8DDD9] last:border-0"
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#1E3F33]/10 flex items-center justify-center flex-shrink-0">
                      <ChevronRight className="w-4 h-4 text-[#1E3F33]" />
                    </div>
                    <p className="text-base sm:text-lg text-[#1E3F33] font-medium leading-relaxed">{item}</p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* ─── DIAGNOSIS ─── */}
        <section className="py-20 md:py-28 bg-[#F2EFE9]" data-testid="service-diagnosis-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
              className="mb-12"
            >
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                Evaluation Before Surgery
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight max-w-2xl" data-testid="service-diagnosis-heading">
                Before planning laparoscopic cancer surgery, evaluation includes:
              </h2>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={stagger}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
            >
              {diagnostics.map((d, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  data-testid={`service-diagnostic-${i}`}
                  className="bg-[#F9F9F6] rounded-2xl p-6 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#1E3F33]/5 transition-all duration-300"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#1E3F33]/10 flex items-center justify-center mb-4">
                    <Search className="w-5 h-5 text-[#1E3F33]" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-[#1E3F33] mb-1">{d.label}</h3>
                  <p className="text-sm text-[#4A6359]">{d.detail}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ─── TREATMENT OPTIONS ─── */}
        <section className="py-20 md:py-28 bg-[#F9F9F6]" data-testid="service-treatment-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
              className="mb-14"
            >
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                Treatment Approach
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight max-w-3xl" data-testid="service-treatment-heading">
                Treatment Options for Laparoscopic cancer surgery in Kolkata
              </h2>
              <p className="text-base sm:text-lg text-[#4A6359] mt-4 max-w-2xl leading-relaxed">
                Treatment depends on the stage of cancer, overall health, and patient preferences            </p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={stagger}
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              {treatments.map((t, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  data-testid={`service-treatment-card-${i}`}
                  className="bg-[#F2EFE9] rounded-2xl md:rounded-[2rem] p-7 md:p-8 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#1E3F33]/5 transition-all duration-300"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-[#1E3F33]/10 flex items-center justify-center">
                      <t.icon className="w-6 h-6 text-[#1E3F33]" />
                    </div>
                    <h3 className="font-serif text-xl sm:text-2xl text-[#1E3F33] leading-snug">{t.title}</h3>
                  </div>
                  <p className="text-sm sm:text-base text-[#4A6359] mb-4 leading-relaxed">{t.description}</p>
                  <ul className="space-y-2.5">
                    {t.points.map((p, j) => (
                      <li key={j} className="flex items-start gap-2.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#8FA998] mt-2 flex-shrink-0" />
                        <span className="text-sm text-[#1E3F33] leading-relaxed">{p}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
        {/* ─── BENEFITS & RISKS OF LAPAROSCOPIC SURGERY ─── */}
        <section
          className="py-20 md:py-28 bg-[#F9F9F6]"
          data-testid="laparoscopic-benefits-section"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={stagger}
            >
              {/* Heading */}
              <motion.div variants={fadeUp} className="mb-12 text-center">
                <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                  Minimally Invasive Cancer Care
                </p>

                <h2 className="font-serif text-4xl sm:text-5xl text-[#1E3F33] tracking-tight leading-tight mb-5">
                  Benefits & Considerations of Laparoscopic Cancer Surgery
                </h2>

                <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed max-w-3xl mx-auto">
                  Laparoscopic surgery offers advanced minimally invasive treatment
                  with faster recovery and reduced discomfort for suitable patients.
                </p>
              </motion.div>

              {/* Two Column Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                {/* BENEFITS */}
                <motion.div
                  variants={fadeUp}
                  className="bg-[#F2EFE9] rounded-2xl md:rounded-[2rem] p-7 md:p-8 border border-[#D8DDD9]"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-full bg-[#1E3F33] flex items-center justify-center">
                      <Heart className="w-5 h-5 text-[#F9F9F6]" />
                    </div>

                    <h3 className="font-serif text-3xl text-[#1E3F33]">
                      Benefits of Laparoscopic Cancer Surgery
                    </h3>
                  </div>

                  <div className="space-y-4">
                    {[
                      "Smaller scars and better cosmetic results",
                      "Less post-operative pain",
                      "Shorter hospital stay",
                      "Faster recovery and return to daily activities",
                      "Lower risk of complications",
                      "Early mobilization",
                    ].map((item, i) => (
                      <div
                        key={i}
                        className="bg-white rounded-xl p-4 border border-[#D8DDD9]"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-6 h-6 rounded-full bg-[#8FA998]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-xs font-bold text-[#1E3F33]">
                              {i + 1}
                            </span>
                          </div>

                          <p className="text-[#1E3F33] text-sm sm:text-base font-medium">
                            {item}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* RISKS */}
                <motion.div
                  variants={fadeUp}
                  className="bg-[#1E3F33] rounded-2xl md:rounded-[2rem] p-7 md:p-8 border border-[#C29F60]/20"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-full bg-[#C29F60]/20 flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-[#C29F60]" />
                    </div>

                    <h3 className="font-serif text-3xl text-[#F9F9F6]">
                      Risks & Considerations
                    </h3>
                  </div>

                  <p className="text-[#8FA998] text-base leading-relaxed mb-6">
                    Although generally safe, laparoscopic surgery requires proper
                    expertise and careful patient selection.
                  </p>

                  <div className="space-y-4 mb-6">
                    {[
                      "Not suitable for all cancer stages",
                      "Requires specialized expertise",
                      "Rare complications like bleeding or infection",
                      "Conversion to open procedure may sometimes be required",
                    ].map((item, i) => (
                      <div
                        key={i}
                        className="bg-[#2C5545] rounded-xl p-4 border border-[#8FA998]/15"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-6 h-6 rounded-full bg-[#C29F60]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-xs font-bold text-[#C29F60]">
                              {i + 1}
                            </span>
                          </div>

                          <p className="text-[#F9F9F6] text-sm sm:text-base font-medium">
                            {item}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-[#2C5545] border border-[#C29F60]/20 rounded-xl p-5">
                    <p className="text-[#F9F9F6] text-sm sm:text-base leading-relaxed">
                      Proper case selection ensures optimal outcomes.
                    </p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>
        {/* ─── RECOVERY TIMELINE ─── */}
        <section className="py-20 md:py-28 bg-[#F2EFE9]" data-testid="service-recovery-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
              className="mb-12"
            >
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                Recovery Journey
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight max-w-2xl" data-testid="service-recovery-heading">
                Recovery Timeline
              </h2>
              <p className="text-base sm:text-lg text-[#4A6359] mt-4 max-w-xl leading-relaxed">
                One of the biggest advantages is faster recovery.
              </p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={stagger}
              className="grid grid-cols-1 md:grid-cols-3 gap-6"
            >
              {recoveryTimeline.map((phase, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  data-testid={`service-recovery-phase-${i}`}
                  className="bg-[#F9F9F6] rounded-2xl p-7 border border-[#D8DDD9]"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-8 h-8 rounded-full bg-[#1E3F33] flex items-center justify-center">
                      <span className="text-xs font-bold text-[#F9F9F6]">{i + 1}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-[#1E3F33]">{phase.phase}</h3>
                  </div>
                  <ul className="space-y-3">
                    {phase.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-2.5">
                        <Clock className="w-4 h-4 text-[#8FA998] mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-[#4A6359] leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ─── FAQ ─── */}
        <section className="py-20 md:py-28 bg-[#F9F9F6]" data-testid="service-faq-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
              >
                <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                  Common Questions
                </p>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight" data-testid="service-faq-heading">
                  Frequently Asked Questions
                </h2>
                <p className="text-base sm:text-lg text-[#4A6359] mt-4 leading-relaxed">
                  Find answers to common questions about ovarian cancer diagnosis, treatment options, and recovery.
                </p>
              </motion.div>

              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
              >
                <Accordion type="single" collapsible className="w-full" data-testid="service-faq-accordion">
                  {faqs.map((faq, i) => (
                    <AccordionItem
                      key={i}
                      value={`faq-${i}`}
                      className="border-b border-[#D8DDD9] py-1"
                      data-testid={`service-faq-item-${i}`}
                    >
                      <AccordionTrigger
                        data-testid={`service-faq-trigger-${i}`}
                        className="font-serif text-lg sm:text-xl text-[#1E3F33] hover:no-underline py-5 text-left"
                      >
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-[#4A6359] text-base leading-relaxed pb-5">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ─── FINAL CTA ─── */}
        <section className="py-20 md:py-28 bg-[#1E3F33]" data-testid="service-final-cta">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
            >
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-6">
                Take the First Step
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F9F9F6] leading-tight max-w-3xl mx-auto mb-6" data-testid="service-final-cta-heading">
                Advanced Minimally Invasive Cancer Surgery in Kolkata
              </h2>
              <p className="text-base sm:text-lg text-[#8FA998] max-w-xl mx-auto leading-relaxed mb-10">
                If you are looking for safe, effective, and minimally invasive cancer treatment, consult Dr. Neha Agarwal for expert laparoscopic gynecologic oncology care in Kolkata.</p>

              <div className="flex flex-wrap justify-center gap-4">
                <a
                  href="/book-appointment"
                  data-testid="service-cta-book-btn"
                  className="inline-flex items-center gap-2 bg-[#F9F9F6] text-[#1E3F33] px-8 py-4 rounded-full text-sm font-bold hover:bg-[#E6E7E2] transition-colors"
                >
                  <Calendar className="w-4 h-4" />
                  Book Appointment
                </a>
                <a
                  href="tel:+916291000829"
                  data-testid="service-cta-call-btn"
                  className="inline-flex items-center gap-2 border-2 border-[#F9F9F6] text-[#F9F9F6] px-8 py-4 rounded-full text-sm font-bold hover:bg-[#F9F9F6] hover:text-[#1E3F33] transition-all"
                >
                  <Phone className="w-4 h-4" />
                  Get Second Opinion
                </a>
                <a
                  href="https://wa.me/916291000829"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="service-cta-whatsapp-btn"
                  className="inline-flex items-center gap-2 bg-[#2C5545] text-[#F9F9F6] px-8 py-4 rounded-full text-sm font-bold hover:bg-[#8FA998]/30 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ─── SEO FOOTER ─── */}
        <footer className="bg-[#1E3F33] border-t border-[#F9F9F6]/10 py-12" data-testid="service-footer">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <h3 className="font-serif text-lg text-[#F9F9F6]/50 mb-3">
              Laparoscopic Gynecologic Cancer Surgeon in Kolkata
            </h3>
            <p className="text-xs text-[#F9F9F6]/30 leading-relaxed max-w-3xl mb-6">
              Looking for laparoscopic cancer surgery in Kolkata? Dr. Neha Agarwal offers advanced minimally invasive surgical treatment for gynecologic cancers across Mukundapur, EM Bypass, Garia, Kasba, Ballygunge, Salt Lake, and nearby areas.
            </p>
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6 border-t border-[#F9F9F6]/10">
              <p className="text-xs text-[#F9F9F6]/30">
                &copy; {new Date().getFullYear()} Dr. Neha Agarwal. All rights reserved.
              </p>
              <Link to="/" className="text-xs text-[#F9F9F6]/30 hover:text-[#F9F9F6]/60 transition-colors flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Back to Home
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
